/**
 * Which door this build is being viewed through, and what that door shows.
 *
 * One codebase, three hosts: go.pdpo.ai (the SME product), draw.pdpo.ai (the
 * public draw router, see drawHost.ts), and campaigns.pdpo.ai — the Campaigns
 * module of PDPO Assist, per the architecture in GO.md. Campaigns is this same
 * app simplified by CONFIG, never by copy: the moment someone forks a
 * "campaigns edition" we own a third drifting tree, which is the
 * consent-catalogue bug with a bigger blast radius.
 *
 * The config cuts what PDPO Assist governs (the wizard, generated documents,
 * the inbox, breach management, destruction) and keeps what happens at the
 * point of collection (draws, capture, the list — renamed Audiences). Two
 * defaults flip: consent forms require approval, and draws require approval.
 *
 * The SME surface is deliberately untouched by all of this. Zero-ceremony
 * capture is Go's product; the ceremony is Campaigns' product. Do not
 * harmonise them.
 */

export const CAMPAIGNS_HOST_BASES = [
  'campaigns.pdpo.ai',
  'pdpo-campaigns.netlify.app',
  'campaigns.localhost',
];

export function isCampaignsHost(hostname: string): boolean {
  return CAMPAIGNS_HOST_BASES.includes(hostname);
}

export type AppSurface = 'go' | 'campaigns';

export function appSurface(hostname: string = window.location.hostname): AppSurface {
  return isCampaignsHost(hostname) ? 'campaigns' : 'go';
}

export interface SurfaceConfig {
  /** Which organisations this door admits. */
  organisationSurface: 'go' | 'main';
  /** Whether the person must hold the CAMPAIGNS authority to enter. */
  requiresAuthority: boolean;
  /** Bottom tab keys shown. Order matters. */
  tabs: ('home' | 'documents' | 'contacts' | 'pipeline' | 'draws' | 'inbox')[];
  /** The CRM: pipeline, leads, tasks and permission-gated messages. */
  pipeline: boolean;
  /** What the contact list is called on this door. */
  contactsLabel: string;
  /** The setup wizard writes processing_activities; Assist orgs have a curated register. */
  wizard: boolean;
  /** Breach reporting: 'manage' is the full log; 'report' is intake only — the
   *  red button files into the same breach_records the DPO works in Assist. */
  breach: 'manage' | 'report';
  /** Whether documents may be destroyed from this door. */
  allowDestruction: boolean;
  /** Default for approval_required on consent forms created here. */
  formsRequireApproval: boolean;
  /** Default for approval_required on draws created here. */
  drawsRequireApproval: boolean;
}

const CONFIGS: Record<AppSurface, SurfaceConfig> = {
  go: {
    organisationSurface: 'go',
    requiresAuthority: false,
    tabs: ['home', 'documents', 'contacts', 'pipeline', 'draws', 'inbox'],
    contactsLabel: 'People',
    pipeline: true,
    wizard: true,
    breach: 'manage',
    allowDestruction: true,
    formsRequireApproval: false,
    drawsRequireApproval: false,
  },
  campaigns: {
    organisationSurface: 'main',
    requiresAuthority: true,
    tabs: ['home', 'contacts', 'draws'],
    contactsLabel: 'Audiences',
    pipeline: false,
    wizard: false,
    breach: 'report',
    allowDestruction: false,
    formsRequireApproval: true,
    drawsRequireApproval: true,
  },
};

export function surfaceConfig(hostname: string = window.location.hostname): SurfaceConfig {
  return CONFIGS[appSurface(hostname)];
}

/** The authority_roles.role_code that opens the campaigns door. */
export const CAMPAIGNS_AUTHORITY_CODE = 'CAMPAIGNS';
